import {
  CreateElement,
  VNode,
  VNodeChildren,
  VNodeChildrenArrayContents,
  VNodeData
} from "vue";

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

export function htmlToVNodes(
  html: string,
  createElement: CreateElement,
  functionToRenderChildren: () => VNode | VNode[],
  {
    enableContentEditableAndFocus,
    saveContentEditable
  }: {
    enableContentEditableAndFocus: Function;
    saveContentEditable: Function;
  }
): VNodeChildrenArrayContents {
  const processNodes = (nodes: NodeListOf<ChildNode>) => {
    const arrayOfNodes: VNodeChildrenArrayContents = [];
    nodes.forEach(node => {
      if (node.nodeType === TEXT_NODE) {
        // nodeValue can have white spaces and we must preserve them, otherwise the HTML may end up wrong
        const nodeValue = node.nodeValue;

        if (nodeValue?.trim() === "%%CHILDREN%%") {
          // '<div> %%CHILDREN%% </div>' will lose the whitespace around children, which is technically a bug.
          // But this probably doesn't matter.
          arrayOfNodes.push(functionToRenderChildren());
        } else if (nodeValue) {
          arrayOfNodes.push(nodeValue);
        }
      } else if (node.nodeType === ELEMENT_NODE && node instanceof Element) {
        /* property attributes actually does exist of nodeType is ELEMENT_NODE, apparently */
        const rawAttributes = node.attributes;
        const tag = node.nodeName;
        const attsObj: VNodeData["attrs"] = {};
        const domProps: VNodeData["domProps"] = {};
        const contentEditableEvents: VNodeData["on"] = {};
        const children: VNodeChildren = [];

        // Don't add class or style if variable is empty (it needs to be undefined as null is considered set)
        let classString: string | undefined = undefined;
        let styleString: string | undefined = undefined;

        for (let i = 0; i < rawAttributes.length; i++) {
          const rawAttribute = rawAttributes.item(i);

          if (rawAttribute) {
            const attributeName = rawAttribute.name;

            if (attributeName === "class") {
              classString = rawAttribute.value;
            } else if (attributeName === "style") {
              styleString = rawAttribute.value;
            } else {
              attsObj[attributeName] = rawAttribute.value;
            }

            /**
             * Once we met content-editable DOM node, there's no point in creating
             * VNodes for its child DOM nodes. Setting innerHTML is a correct
             * workaround for this bug:
             *
             * https://github.com/vuejs/vue/issues/5805#issuecomment-305951126
             */
            if (attributeName === "data-content-editable-property-path") {
              contentEditableEvents.dblclick = enableContentEditableAndFocus;
              contentEditableEvents.blur = saveContentEditable;
              domProps.innerHTML = node.innerHTML;
            }
          }
        }

        if (!domProps.innerHTML && node.hasChildNodes()) {
          children.push(...processNodes(node.childNodes));
        }

        arrayOfNodes.push(
          createElement(
            tag,
            {
              class: classString,
              style: styleString,
              attrs: { ...attsObj },
              on: { ...contentEditableEvents },
              domProps
            },
            children
          )
        );
      }
    });

    return arrayOfNodes;
  };

  const domParser = new DOMParser();

  // decode the HTML to fix issues like HTML entities '&pound;'
  const decodedHtml = domParser.parseFromString(html, "text/html").body;
  // Then XML serialize it because we need it to be xhtml to process it and the HTML may not be valid XML (like with img closing tags)
  const serializedXml = new XMLSerializer().serializeToString(decodedHtml);
  // remove the body so that we only get the innerHTML. Since this is an XML string, I didn't find any easier way.
  const xmlWithoutBodyTag = serializedXml
    // remove the body tags
    // '.*?' = match everything until the next match,  which is > (non-greedy catch all)
    .replace(/<body.*?>/, "")
    .replace("</body>", "");

  const doc = domParser.parseFromString(
    "<root>" + xmlWithoutBodyTag + "</root>",
    "application/xhtml+xml"
  );

  return processNodes(doc.documentElement.childNodes);
}
